import React, { useState, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import InputField   from 'src/components/InputField'
import CheckBox     from 'src/components/CheckBox'
import Validation   from 'src/components/Validation'
import InvalidForm  from 'src/components/InvalidForm'

const usePlugins = ({
  i18n, currency, plugins, paymentMethod, paymentMethods, session,
  showInputLabels,
}) => {
  const [ datasets, setDatasets ] = useState([])
  const [ errorSets, setErrorSets ] = useState([])
  const [ disabledPlugins, setDisabledPlugins ] = useState([])

  const logErrorAndDisablePlugin = i => (error, context) => {
    console.error(error, context)
    Honeybadger.notify(error, { context })
    if (!session.staff && i != null)
      setDisabledPlugins(disabledPlugins => disabledPlugins.concat(i))
  }

  const collectDataToArray = collectData => {
    if (!collectData || collectData == 'none') return []
    if (collectData == 'all') return Object.keys(paymentMethods)
    if (/^only:[a-z_,]+$/.test(collectData))
      return collectData.split(':')[1].split(',')
    if (/^except:[a-z_,]+$/.test(collectData)) {
      const list = collectData.split(':')[1].split(',')
      return Object.keys(paymentMethods).filter(key => !list.includes(key))
    }
  }

  const collectDataLists = useMemo(() =>
    plugins.map((plugin, i) => {
      const list = collectDataToArray(plugin.collectData)
      if (list) return list

      if (session.staff)
        setErrorSets(errorSets => Object.assign([], errorSets, { [i]: {
          ...errorSets[i],
          plugin: [ <><b>Staff:</b> Plugin has invalid 'collectData' property</> ],
        }}))
      else
        setDisabledPlugins(disabledPlugins => disabledPlugins.concat(i))

      return []
    })
  , [ plugins, paymentMethods ])
  const collectData = useMemo(() =>
    new Set(collectDataLists.reduce((a, list) => a.concat(list), []))
  , [ plugins, paymentMethods ])

  const dataParam = datasets.length ? { __value: datasets } : null

  const validate = props => {
    const errorSets = plugins.map((plugin, i) => {
      if (disabledPlugins.includes(i)) return

      try {
        const errors = plugin.validate?.({
          ...props,
          paymentMethod,
          data: {
            ...datasets[i],
            validate: fieldSet =>
              Validation(i18n, currency).validate(datasets[i], { fieldSet }).errors
            ,
          }
        })

        if (errors && typeof errors == 'object' && Object.keys(errors).length)
          return errors
      } catch (error) {
        logErrorAndDisablePlugin(null)(error, plugin)
        return {
          plugin: [
            <span>
              Something went wrong while processing your donation.{' '}
              <a href="https://help.pledgeling.com/support/tickets/new">
                Please contact support.
              </a>
            </span>
          ]
        }
      }
    })

    setErrorSets(errorSets)
    return !errorSets.filter(Boolean).length
  }

  // Using paymentMethod from state creates some weird race condition that
  // crashes the widget when plugins rely on payment method specific APIs.
  const renderHook = (name, paymentMethod, props) =>
    plugins.map((plugin, i) => {
      // TODO: Replace with error after updating all plugins
      if (!plugin.collectData && paymentMethod != 'fiat') return null
      if (disabledPlugins.includes(i)) return null

      const Component = plugin.hooks?.[name]
      if (!Component) return null

      const setErrors = value => {
        setErrorSets(errorSets => Object.assign([], errorSets, {
          [i]: typeof value == 'function' ? value(errorSets[i]) : value
        }))
      }

      const resetErrorsFor = key => {
        setErrors(errors => ({ ...errors, [key]: null }))
      }

      const showDataApiWarning = () => {
        if (collectDataLists[i].includes(paymentMethod)) return

        if (session.staff)
          setErrors(errors => ({ ...errors, plugin: [
            <><b>Staff:</b> Plugin is not configured to collect data for this payment method</>
          ] }))
        else
          setDisabledPlugins(disabledPlugins => disabledPlugins.concat(i))
      }

      const setData = value => {
        showDataApiWarning()
        setDatasets(datasets => Object.assign([], datasets, {
          [i]: typeof value == 'function' ? value(datasets[i]) : value
        }))
      }

      const data = {
        ...datasets[i],
        get (key, defaultValue = '') {
          showDataApiWarning()
          return datasets[i]?.[key] || defaultValue
        },
        set (key, value) {
          resetErrorsFor(key)
          setData(data => ({ ...data, [key]: value }))
        },
      }

      const errors = { ...errorSets[i] }
      Object.defineProperty(errors, 'firstFor', { value: key => errors[key]?.[0] })
      Object.defineProperty(errors, 'resetFor', { value: resetErrorsFor })

      const setDataDefaults = value =>
        setData(data => ({ ...value, ...data }))

      return (
        <ErrorBoundary
          key={i}
          onError={logErrorAndDisablePlugin(i)}
          fallbackRender={() =>
            session.staff &&
            <div className="alert alert-warning">
              Plugin hook <code>{name}</code> has crashed. See console for details.
            </div>
          }
        >
          <Component
            {...props}
            InputField={InputField}
            CheckBox={CheckBox}
            paymentMethod={paymentMethod}
            showInputLabels={showInputLabels}
            data={data}
            setData={setData}
            setDataDefaults={setDataDefaults}
            errors={errors}
          />
        </ErrorBoundary>
      )
    }).filter(Boolean)

  const renderError = () =>
    <InvalidForm>
      {errorSets.find(errors => errors?.plugin)?.plugin[0]}
    </InvalidForm>

  return { dataParam, collectData, renderHook, renderError, validate }
}

export default usePlugins
